/**
 * Errors thrown INTO the page by the host browser, which Jana neither causes nor
 * can fix (JANA-NEXTJS-R, JANA-NEXTJS-Z, JANA-NEXTJS-W, JANA-NEXTJS-1D).
 *
 * A storefront link shared on Instagram, Facebook or Threads opens in that app's
 * embedded WebView, and those inject a native bridge into every page they load.
 * The bridge outlives its native half: on iOS the injected script reaches for
 * `window.webkit.messageHandlers` after the host tore it down, and on Android
 * the injected Java object is collected while a `postMessage` is in flight. Both
 * surface as uncaught errors attributed to whatever page happened to be open, so
 * they read as storefront defects on product and account routes.
 *
 * ⚠️ **Verified absent from this tree before being dropped, not assumed.**
 * Nothing under `src/` names `window.webkit`, `messageHandlers`, or posts to a
 * native bridge, so no Jana code path can raise either of these. If that stops
 * being true, these patterns begin hiding a real failure and have to go.
 *
 * ⚠️ **This list is for a foreign runtime's own failures and nothing else.** Not
 * for errors that are merely frequent, ugly, or hard to act on. The error sink
 * is the only durable record there is, and an entry here removes events from it
 * silently, with no count anywhere to notice the loss by. `isBenign` in
 * `report.ts` carries the same warning for the same reason.
 *
 * ⚠️ It cannot filter the error boundaries' reports, which do not travel through
 * the browser SDK at all: they POST to `/api/report-error` and are captured on
 * the server. See `report-client.ts`.
 *
 * ⚠️ Its own module so `scripts/assert-benign-errors.ts` can read it without
 * importing `instrumentation-client.ts`, which calls `Sentry.init()` at module
 * scope. A gate that has to boot the SDK to read a list would not be run.
 *
 * ⚠️ **Anchored at the START, and the unanchored version was wrong.** Sentry
 * tests each pattern against the bare exception value as well as against
 * `${type}: ${value}` (`@sentry/core`'s `getPossibleEventMessages`), so `^` does
 * match. Written as bare substrings first, these also swallowed
 * "WebView bridge handshake failed before window.webkit.messageHandlers was
 * read" and "Java object is gone, so the share sheet fell back to the clipboard
 * and that failed too" — Jana errors that NAME the foreign API while reporting
 * something real, which is the exact shape someone adding a WebView feature
 * detect would write. `scripts/assert-benign-errors.ts` holds both as controls
 * and went red on them. Same reasoning, and the same fix, as `isBenign`'s
 * prefix match.
 *
 * Regexes rather than strings because a `string` entry is a substring test in
 * Sentry, which is the width this pair must not have.
 */
export const FOREIGN_RUNTIME_ERRORS = [
	// iOS in-app WebView (WebKit wording), its bridge already torn down.
	/^undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
	// Android WebView, its injected object already collected.
	/^Error invoking postMessage: Java object is gone/,
];

/**
 * The predicate Sentry applies, spelled out so the gate can assert behaviour
 * rather than match this file's text (the reason `borrowStarved` is exported
 * from `prisma/pool.ts`).
 */
export function isForeignRuntimeError(message: string): boolean {
	return FOREIGN_RUNTIME_ERRORS.some((p) => p.test(message));
}
